import { Settings, Shield } from 'lucide-react'
import type React from 'react'

// Extend window interface
declare global {
  interface Window {
    railsData?: {
      csrfToken?: string
      [key: string]: unknown
    }
  }
}

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useLanguage } from '@/landing/contexts/LanguageContext'
import type { UserMenuProps } from '@/types/components'

export const UserMenu: React.FC<UserMenuProps> = ({ currentUser }) => {
  const { t } = useLanguage()

  // Get user initials
  const getInitials = (name: string, email: string) => {
    if (name) {
      const nameParts = name.trim().split(' ')
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
      }
      return nameParts[0][0].toUpperCase()
    }
    return email ? email[0].toUpperCase() : 'U'
  }

  if (!currentUser) {
    return null
  }

  const initials = getInitials(currentUser.full_name || '', currentUser.email || '')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
            {initials}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56 p-2 border-gray-200 shadow-none" align="end">
        <div className="px-3 py-2 mb-2 rounded-md bg-muted/50">
          <p className="text-sm font-medium">{currentUser.full_name || currentUser.email}</p>
          {currentUser.full_name && currentUser.email && (
            <p className="text-xs text-muted-foreground">{currentUser.email}</p>
          )}
        </div>
        <DropdownMenuItem asChild>
          <a
            href="/admin"
            className="w-full flex items-center cursor-pointer rounded-md p-3 hover:bg-accent transition-colors"
          >
            <Shield className="mr-2 h-4 w-4" />
            <span className="font-medium">{t('user.admin_panel')}</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href="/settings"
            className="w-full flex items-center cursor-pointer rounded-md p-3 hover:bg-accent transition-colors"
          >
            <Settings className="mr-2 h-4 w-4" />
            <span className="font-medium">{t('user.settings')}</span>
          </a>
        </DropdownMenuItem>
        <div className="h-px bg-border my-1"></div>
        <DropdownMenuItem asChild>
          <a
            href="/logout"
            data-method="post"
            className="w-full cursor-pointer rounded-md p-3 hover:bg-accent transition-colors"
          >
            <span className="font-medium">{t('user.logout')}</span>
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
